import React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

import { Button } from '@components/ui/button';
import { cn } from '@components/lib/utils';

import {
  ImportSpreadsheetButton,
  useCanImportCreators,
} from './ImportSpreadsheet';

export const CREATORS_TABS = [
  { to: '/creators', label: 'All creators' },
  { to: '/search', label: 'Find new' },
  { to: '/saved-creators', label: 'Lists' },
];

/** `/creators?view=suggested`: the full suggestions list. */
export const SUGGESTED_VIEW = 'suggested';
const SUGGESTED_PATH = '/creators';

const tabClass = (active: boolean) =>
  cn(
    'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold',
    active
      ? 'border-marble-blue-500 text-foreground'
      : 'border-transparent text-marble-bg-500 hover:text-foreground',
  );

const isActive = (pathname: string, to: string) =>
  pathname === to || pathname.startsWith(`${to}/`);

/** Sub-navigation shared by the three Creators screens (/creators, /search,
 * /saved-creators), so they read as one area. Import sits here on purpose:
 * "how do I bring my spreadsheet" is the first question, on every tab. */
export const CreatorsNav: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const suggested =
    pathname === SUGGESTED_PATH && params.get('view') === SUGGESTED_VIEW;
  const onSearch = isActive(pathname, '/search');
  const canImport = useCanImportCreators();

  return (
    <div
      className={cn(
        'mb-6 flex flex-col gap-3 border-b border-border sm:flex-row sm:items-end sm:justify-between',
        className,
      )}
    >
      <nav aria-label="Creators" className="-mb-px flex overflow-x-auto">
        {CREATORS_TABS.map((tab) => {
          const active = isActive(pathname, tab.to) && !suggested;
          return (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={active ? 'page' : undefined}
              className={tabClass(active)}
            >
              {tab.label}
            </Link>
          );
        })}
        <Link
          to={`${SUGGESTED_PATH}?view=${SUGGESTED_VIEW}`}
          aria-current={suggested ? 'page' : undefined}
          className={tabClass(suggested)}
        >
          Suggested
        </Link>
      </nav>

      <div className="flex flex-wrap items-center gap-2 pb-2">
        {canImport && <ImportSpreadsheetButton />}
        {!onSearch && (
          <Button asChild className="cursor-pointer">
            <Link to="/search">
              <Sparkles />
              Find creators
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreatorsNav;
